import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// Contract signing interfaces
interface ContractSignRequest {
  digital_signature: string;
  signature_date: string;
  agrees_to_terms: boolean;
}

interface ContractSignResponse {
  success: boolean;
  message: string;
  data?: {
    contract_id: number;
    signed_at: string;
  };
}

interface ContractStatusResponse {
  success: boolean;
  data: {
    contract_signed: boolean;
    signed_at?: string;
    digital_signature?: string;
    contract_version?: string;
  };
}

// Professional profile interfaces
interface ProfessionalProfileRequest {
  // Personal Information
  profile_photo?: File;
  introduction?: string;
  /** FO stap 3 — "Mijn Aanpak" (medewerkerportaal) */
  bio_medewerker?: string;
  /** FO stap 3 — "Wat ik voor uw organisatie beteken" (HR-dashboard) */
  bio_werkgever?: string;
  first_name: string;
  last_name: string;
  birth_date: string;
  phone: string;
  email: string;
  
  // Address
  street_address: string;
  postal_code: string;
  city: string;
  country: string;
  
  // Professional Details — tarief wordt automatisch bepaald (KvK-datum + type),
  // dus het formulier stuurt het niet mee.
  years_self_employed: number;
  hourly_rate?: number;
  kvk_nummer?: string;
  kvk_registration_date?: string;
  kvk_registration_years?: number;
  
  // Availability — uren per week / werkwijze / auto zijn vervallen (FO, Wet DBA):
  // de geopende agenda-tijdslots bepalen de beschikbaarheid.
  availability_status: 'not_available' | 'immediately' | 'specific_date';
  available_from_date?: string;
  availability_slots?: Array<{
    weekday: number;
    start_time: string;
    end_time: string;
    slot_type?: 'intake' | 'sessie' | 'beide';
  }>;

  // Werkgebied (dynamische reisradius; null = heel Nederland)
  reisradius_km?: number | null;

  // Dubbele status-grendel (migratie 111): handmatige cliëntenstop voor
  // nieuwe pools en de capaciteitslimiet over alle opdrachtgevers heen
  accepteer_nieuwe_pools?: boolean;
  max_actieve_trajecten?: number;
  /** 100% online werkwijze (migratie 112) — BLAUW-status, geen reiskosten */
  werkt_alleen_online?: boolean;

  // Other
  show_employers_on_profile?: boolean;

  // Arrays
  languages: string[];
  /** Legacy provincie-selectie; vervangen door reisradius_km */
  active_regions?: string[];
  affinity?: string[]; // Renamed from industry_experience
  /** FO stap 4 — expertise-tags en doelgroep-niveaus voor de HR-zoekmachine */
  specialisatie_tags?: string[];
  doelgroep_niveaus?: string[];
  /** FO stap 7 — YouTube/Vimeo-link als alternatief voor de video-upload */
  intro_video_url?: string;
  language_knowledge_explanation?: string;
  work_experience: Array<{
    company: string;
    position: string;
    start_date: string;
    end_date?: string;
    current: boolean;
    description: string;
  }>;
  education: Array<{
    institution: string;
    degree: string;
    field: string;
    start_year: number;
    end_year?: number;
    current: boolean;
  }>;
}

interface ProfessionalProfileResponse {
  success: boolean;
  message: string;
  data?: {
    profile_id: number;
  };
}

export interface AvailabilitySlot {
  id?: number;
  weekday: number;
  start_time: string;
  end_time: string;
  slot_type?: 'intake' | 'sessie' | 'beide';
}

export interface PortfolioStatus {
  items: Array<{ key: string; label: string; complete: boolean }>;
  percentage: number;
  complete: boolean;
  published: boolean;
  published_at: string | null;
  availability_indicator: 'direct' | 'beperkt' | 'geen';
  open_hours_per_week: number;
  slot_count: number;
}

interface ProfileStatusResponse {
  success: boolean;
  data: {
    profile_complete: boolean;
    profile?: ProfessionalProfileRequest & {
      id: number;
      created_at: string;
      updated_at: string;
      intro_video_path?: string | null;
      profile_photo_path?: string | null;
      member_number?: string | null;
      screening_status?: string | null;
      professional_types_from_screening?: string[];
      /** Legacy kolomnaam; hernoemd naar `affinity` */
      industry_experience?: string[];
      reisradius_km?: number | null;
    };
    availability_slots?: AvailabilitySlot[];
    portfolio_status?: PortfolioStatus;
  };
}

// Contract API hooks
export const useContractSign = () => {
  return useMutation<ContractSignResponse, Error, ContractSignRequest>({
    mutationFn: async (contractData) => {
      const response = await api.post('/professional/contract/sign', contractData);
      return response.data;
    }
  });
};

export const useContractStatus = () => {
  return useQuery<ContractStatusResponse>({
    queryKey: ['contract-status'],
    queryFn: async () => {
      const response = await api.get('/professional/contract/status');
      return response.data;
    }
  });
};

// Profile API hooks
export const useCreateProfile = () => {
  return useMutation<ProfessionalProfileResponse, Error, ProfessionalProfileRequest>({
    mutationFn: async (profileData) => {
      const formData = new FormData();
      
      // Add all form fields
      Object.entries(profileData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'profile_photo' && value instanceof File) {
            formData.append(key, value);
          } else if (Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else if (typeof value === 'boolean') {
            formData.append(key, value.toString());
          } else {
            formData.append(key, value.toString());
          }
        }
      });
      
      const response = await api.post('/professional/profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    }
  });
};

export const useProfileStatus = () => {
  return useQuery<ProfileStatusResponse>({
    queryKey: ['profile-status'],
    queryFn: async () => {
      const response = await api.get('/professional/profile');
      return response.data;
    }
  });
};

/**
 * 'Profiel Compleet'-check (FO stap 9): welke onderdelen nog ontbreken,
 * het percentage voor de progressiebalk en de beschikbaarheidsindicator.
 */
export const usePortfolioStatus = () => {
  return useQuery<PortfolioStatus>({
    queryKey: ['portfolio-status'],
    queryFn: async () => {
      const response = await api.get('/professional/portfolio-status');
      return response.data.data;
    }
  });
};

/** Definitieve publicatie van het portfolio (FO stap 10) */
export const usePublishPortfolio = () => {
  const queryClient = useQueryClient();
  return useMutation<PortfolioStatus, Error, void>({
    mutationFn: async () => {
      const response = await api.post('/professional/portfolio/publish');
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolio-status'] });
      queryClient.invalidateQueries({ queryKey: ['profile-status'] });
    }
  });
};
